import React, { useEffect, useState } from 'react';
import mammoth from 'mammoth';

const privacyPolicyTables = [
  {
    headers: ['Sr No', 'Permission', 'Description', 'Purpose'],
    rows: 9,
  },
  {
    headers: ['Sr No', 'Kind of Information', 'Retention Period'],
    rows: 5,
  },
];

function normalizeTableText(value) {
  return value.replace(/\s+/g, ' ').trim();
}

function createCell(doc, tag, content) {
  const cell = doc.createElement(tag);
  cell.textContent = normalizeTableText(content);
  return cell;
}

function replaceParagraphTable(doc, { headers, rows }) {
  const paragraphs = Array.from(doc.querySelectorAll('p'));
  const start = paragraphs.findIndex((paragraph, index) =>
    headers.every((header, offset) => {
      const candidate = paragraphs[index + offset];
      return candidate && normalizeTableText(candidate.textContent) === header;
    })
  );

  if (start === -1) return;

  const columnCount = headers.length;
  const tableParagraphs = paragraphs.slice(start, start + columnCount + rows * columnCount);
  if (tableParagraphs.length !== columnCount + rows * columnCount) return;

  const scrollContainer = doc.createElement('div');
  scrollContainer.className = 'legal-table-scroll';

  const table = doc.createElement('table');
  const thead = doc.createElement('thead');
  const headerRow = doc.createElement('tr');
  headers.forEach((header) => headerRow.appendChild(createCell(doc, 'th', header)));
  thead.appendChild(headerRow);
  table.appendChild(thead);

  const tbody = doc.createElement('tbody');
  for (let rowIndex = 0; rowIndex < rows; rowIndex += 1) {
    const row = doc.createElement('tr');
    for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
      const cellIndex = columnCount + rowIndex * columnCount + columnIndex;
      row.appendChild(createCell(doc, 'td', tableParagraphs[cellIndex].textContent));
    }
    tbody.appendChild(row);
  }
  table.appendChild(tbody);
  scrollContainer.appendChild(table);

  tableParagraphs[0].replaceWith(scrollContainer);
  tableParagraphs.slice(1).forEach((paragraph) => paragraph.remove());
}

function enhanceLegalDocumentHtml(rawHtml, file) {
  if (!file.toLowerCase().includes('privacy-policy') || typeof DOMParser === 'undefined') {
    return rawHtml;
  }

  const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
  privacyPolicyTables.forEach((tableConfig) => replaceParagraphTable(doc, tableConfig));

  return doc.body.innerHTML;
}

export default function V2LegalDocumentPage({ title, file, heroTitle }) {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadDocument() {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(file);
        if (!response.ok) {
          throw new Error('Document could not be loaded.');
        }

        const arrayBuffer = await response.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });

        if (!cancelled) {
          setHtml(enhanceLegalDocumentHtml(result.value, file));
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Document could not be loaded.');
          setLoading(false);
        }
      }
    }

    loadDocument();

    return () => {
      cancelled = true;
    };
  }, [file]);

  return (
    <main>
      <section className="page-hero">
        <div className="wrap">
          <span className="v2-badge">
            <span className="dot" />
            Legal
          </span>
          <h1>
            {heroTitle.map((part, index) =>
              typeof part === 'string' ? (
                <React.Fragment key={index}>{part}</React.Fragment>
              ) : (
                <span key={index} className="it">
                  {part.it}
                </span>
              )
            )}
          </h1>
        </div>
      </section>

      <section>
        <div className="wrap">
          {loading && <p className="legal-doc-status">Loading document…</p>}
          {error && (
            <div className="prose">
              <p>{error}</p>
              <p>
                <a href={file} download>
                  Download {title}
                </a>
              </p>
            </div>
          )}
          {!loading && !error && (
            <div
              className="prose legal-doc"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}
        </div>
      </section>
    </main>
  );
}
